import React, { useEffect } from 'react'
import { ChildProps, graphql } from 'react-apollo'
import {
  Seller,
  SellersByDomainResponse,
  getSellersByDomainQuery,
} from '../../../../Api/schain'
import {
  IComboBoxOption,
  ITag,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
} from 'office-ui-fabric-react'
import { TagInput } from '../../../../Components/Input/TagInput'

interface InputProps {
  domain: string
  disabled: boolean
  selectedSellerId: string
  onChange: (change: Change) => any
  label: string
  previousNode: {
    label: string
    domain?: string
  }
}

interface Change {
  selectedSellerId?: string | null
  error?: string
  isPending?: boolean
  seller?: Seller
}

function SellersComboBoxView(
  props: ChildProps<InputProps, SellersByDomainResponse>
) {
  const {
    data,
    onChange,
    selectedSellerId,
    disabled,
    label,
    previousNode,
    domain,
  } = props
  const { error: queryError, loading } = data!
  const hasValidEntries = !queryError && !loading && data
  const entries = hasValidEntries ? data!.sellersByDomain || [] : []

  const toOption = (entry: Seller) => {
    const key = entry.seller_id
    if (entry.is_confidential) {
      return {
        key,
        text: `Confidential Seller #${entry.seller_id}`,
      }
    }
    if (!!entry.domain && !!entry.name) {
      return {
        key,
        text: `${entry.domain} / ${entry.name}`,
      }
    }
    if (!!entry.domain) {
      return {
        key,
        text: entry.domain,
      }
    }
    return {
      key,
      text: entry.name || entry.seller_id,
    }
  }

  const alphabeticalSort = (a: IComboBoxOption, b: IComboBoxOption) =>
    a.text.localeCompare(b.text, undefined, { numeric: true })
  const previousNodeEntry = previousNode.domain
    ? entries.find((entry) =>
        /r2b2\.(cz|io)/.test(previousNode.domain!)
          ? /r2b2\./.test(entry.domain || '') &&
            /r2b2\./.test(previousNode.domain!)
          : entry.domain === previousNode.domain
      )
    : null
  const entriesCanContainPreviousNode =
    !previousNode.domain ||
    !!previousNodeEntry ||
    entries.some(({ is_confidential }) => !!is_confidential)
  const errorMessage =
    !!entries.length && !entriesCanContainPreviousNode
      ? 'The selected sellers.json can not be used as it doesn’t contain any node that can be connected to the existing chain.'
      : null
  useEffect(() => {
    if (previousNodeEntry && !selectedSellerId) {
      onChange({
        selectedSellerId: previousNodeEntry.seller_id.toString(),
        seller: previousNodeEntry,
      })
    }
  }, [previousNodeEntry, selectedSellerId, onChange])
  const tags: ITag[] = unique(
    entries
      .filter((entry) =>
        previousNode.domain && !previousNodeEntry ? entry.is_confidential : true
      )
      .map(toOption)
  )
    .sort(alphabeticalSort)
    .map((option) => ({ key: option.key.toString(), name: option.text }))
  const selectedTag = tags.find((tag) => tag.key === selectedSellerId)
  const resolveSuggestions = (filter: string): ITag[] => {
    const available = tags.filter((tag) => tag.key !== selectedSellerId)
    const normalized = filter.trim().toLowerCase()
    return normalized
      ? available.filter((tag) => tag.name.toLowerCase().includes(normalized))
      : available
  }
  return (
    <Stack gap={8}>
      {errorMessage ? (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={true}
          styles={{ root: { marginTop: 8 } }}
        >
          {errorMessage}
        </MessageBar>
      ) : null}
      <TagInput
        required
        label={label}
        itemLimit={1}
        disabled={
          disabled ||
          !hasValidEntries ||
          !entriesCanContainPreviousNode ||
          !!previousNodeEntry
        }
        selectedItems={selectedTag ? [selectedTag] : []}
        onResolveSuggestions={resolveSuggestions}
        onChange={(items?: ITag[]) => {
          const selectedItem = items && items[0]
          if (!selectedItem) {
            onChange({ selectedSellerId: null })
            return
          }
          const selectedEntry = entries.find(
            (entry) => entry.seller_id === selectedItem.key
          )
          onChange({
            selectedSellerId: selectedItem.key,
            seller: selectedEntry,
          })
        }}
        pickerLabels={{
          suggestionsHeader: '',
          noResults: 'No sellers found',
        }}
      />
      <Spinner
        size={SpinnerSize.small}
        style={{
          visibility: !!domain && loading ? 'visible' : 'hidden',
          height: 32,
          position: 'absolute',
          bottom: 26,
          right: 8,
        }}
      />
      {!!domain && !!queryError ? (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={true}>
          No sellers found for this domain
        </MessageBar>
      ) : null}
    </Stack>
  )
}

const withGql = graphql<InputProps, SellersByDomainResponse>(
  getSellersByDomainQuery,
  {
    options: ({ domain }) => ({
      notifyOnNetworkStatusChange: true,
      variables: {
        domain,
      },
    }),
  }
)
export const SellersComboBox = withGql(SellersComboBoxView)

function unique(array: IComboBoxOption[]): IComboBoxOption[] {
  return array.filter(
    (value, index, self) => self.findIndex((t) => t.key === value.key) === index
  )
}
